/*
This file contains a context provider which provides a common api, data, and actions related to docket,contract and xero invoice.
*/

import React from "react";

import useGetFilter, { filterItem } from "@/hooks/useGetFilter";

//Types
type InvoiceServiceContextType = {
  allProjectsList: filterItem[];
  customerList: filterItem[];
  projectList: filterItem[];
};

type InvoiceServiceProviderProps = {
  children: React.ReactNode;
};

type ReturnType = {
  InvoicingServiceContextProvider: React.FC<InvoiceServiceProviderProps>;
  useInvoiceServiceContext: () => InvoiceServiceContextType;
};

const createInvoicingServiceContext = (): ReturnType => {
  const InvoicingServiceContext = React.createContext<
    InvoiceServiceContextType | undefined
  >(undefined);

  const InvoicingServiceContextProvider: React.FC<
    InvoiceServiceProviderProps
  > = ({ children }) => {
    const { customerList, allProjectsList, projectList } = useGetFilter([
      "customer",
      "allProject",
      "project",
    ]);

    return (
      <InvoicingServiceContext.Provider
        value={{
          customerList,
          allProjectsList,
          projectList,
        }}
      >
        {children}
      </InvoicingServiceContext.Provider>
    );
  };

  const useInvoiceServiceContext = (): InvoiceServiceContextType => {
    const context = React.useContext(InvoicingServiceContext);
    if (!context) {
      throw new Error(
        "useInvoiceServiceContext must be used within a InvoicingServiceContextProvider",
      );
    }

    return context;
  };

  return {
    InvoicingServiceContextProvider,
    useInvoiceServiceContext,
  };
};

const { InvoicingServiceContextProvider, useInvoiceServiceContext } =
  createInvoicingServiceContext();

export { InvoicingServiceContextProvider, useInvoiceServiceContext };
